// PNG Export functionality
export const exportSelectionToPNG = (
  svgElement: SVGSVGElement,
  fileName?: string
): void => {
  if (!svgElement) {
    alert('Canvas not available for export');
    return;
  }

  try {
    // Debug: Check what's actually in the original SVG
    console.log('=== PNG EXPORT DEBUG ===');
    console.log('SVG element:', svgElement);
    console.log('SVG HTML length:', svgElement.outerHTML.length);
    console.log(
      'Total text elements in SVG:',
      svgElement.querySelectorAll('text').length
    );
    console.log(
      'Foreign objects in SVG:',
      svgElement.querySelectorAll('foreignObject').length
    );

    // List all text elements and their content
    const textElements = svgElement.querySelectorAll('text');
    textElements.forEach((text, index) => {
      console.log(
        `Text element ${index}:`,
        text.textContent,
        text.getBoundingClientRect()
      );
    });

    // Get dimensions from the SVG element
    const rect = svgElement.getBoundingClientRect();
    const exportWidth = rect.width;
    const exportHeight = rect.height;

    // Create a clean SVG with only safe elements (no foreignObject)
    const cleanSvg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg'
    );
    cleanSvg.setAttribute('width', exportWidth.toString());
    cleanSvg.setAttribute('height', exportHeight.toString());
    cleanSvg.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`);
    cleanSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    // For PNG export, we need to handle fonts differently since canvas conversion doesn't reliably load web fonts
    // Instead, we'll check what fonts are actually available and adjust accordingly
    console.log('PNG Export: Checking available fonts...');

    // Add background
    const background = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'rect'
    );
    background.setAttribute('x', '0');
    background.setAttribute('y', '0');
    background.setAttribute('width', exportWidth.toString());
    background.setAttribute('height', exportHeight.toString());
    background.setAttribute('fill', '#1a1a1a');
    cleanSvg.appendChild(background);

    // Get the main transform group and copy all non-foreignObject children
    const mainGroup = svgElement.querySelector('g[transform]');
    console.log('Main group found:', !!mainGroup);
    console.log('Main group children count:', mainGroup?.children.length);
    console.log(
      'Text elements in main group:',
      mainGroup?.querySelectorAll('text').length
    );

    if (mainGroup) {
      // Clone the main group but filter out problematic elements
      const cleanGroup = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'g'
      );
      cleanGroup.setAttribute(
        'transform',
        mainGroup.getAttribute('transform') || ''
      );

      // Copy defs (patterns, etc.) first
      const defs = mainGroup.querySelector('defs');
      if (defs) {
        const cleanDefs = defs.cloneNode(true) as SVGDefsElement;
        cleanGroup.appendChild(cleanDefs);
      }

      // Copy grid background
      const gridRect = mainGroup.querySelector('rect[fill="url(#grid)"]');
      if (gridRect) {
        const cleanGridRect = gridRect.cloneNode(true) as SVGRectElement;
        cleanGroup.appendChild(cleanGridRect);
      }

      // Copy all other children, with special handling for different types
      Array.from(mainGroup.children).forEach(child => {
        console.log(
          'Processing main group child:',
          child.tagName,
          child.getAttribute('class')
        );

        if (
          child.tagName === 'defs' ||
          child.getAttribute('fill')?.includes('url(#grid)')
        ) {
          // Skip - already handled above
          return;
        }

        const cleanedChild = cleanChildRecursively(child as SVGElement);
        if (cleanedChild) {
          cleanGroup.appendChild(cleanedChild);
        }
      });

      cleanSvg.appendChild(cleanGroup);
    }

    // Convert to data URL and create image
    const svgData = new XMLSerializer().serializeToString(cleanSvg);
    console.log('PNG SVG data includes Caveat:', svgData.includes('Caveat'));

    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // Small delay to allow fonts to load
      setTimeout(() => {
        const canvas = document.createElement('canvas');
        canvas.width = exportWidth;
        canvas.height = exportHeight;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(blob => {
            if (blob) {
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = fileName || `canvas-export-${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(link.href);
            }
          }, 'image/png');
        }

        URL.revokeObjectURL(url);
      }, 100);
    };

    img.onerror = error => {
      console.error('Failed to load SVG for PNG conversion:', error);
      alert(
        'Failed to export PNG. The canvas may contain unsupported content.'
      );
      URL.revokeObjectURL(url);
    };

    img.src = url;
  } catch (error) {
    console.error('PNG export failed:', error);
    alert('Failed to export PNG: ' + (error as Error).message);
  }
};

// Helper function to recursively clean SVG children
function cleanChildRecursively(element: SVGElement): SVGElement | null {
  // Skip foreignObject elements completely for PNG export to avoid tainted canvas
  if (element.tagName === 'foreignObject') {
    const fo = element as SVGForeignObjectElement;

    // Handle different types of foreign object content
    const img = fo.querySelector('img');
    const svgContent = fo.querySelector('svg');
    const iconDiv = fo.querySelector('[data-lucide]');
    const textContent = fo.textContent?.trim();

    console.log('Processing foreignObject in PNG export:', {
      hasImg: !!img,
      hasSvg: !!svgContent,
      hasIcon: !!iconDiv,
      hasText: !!textContent,
      textContent: textContent?.substring(0, 100),
      x: fo.getAttribute('x'),
      y: fo.getAttribute('y'),
      width: fo.getAttribute('width'),
      height: fo.getAttribute('height'),
    });

    if (svgContent) {
      // Try to extract SVG content and convert to native SVG
      try {
        const svgClone = svgContent.cloneNode(true) as SVGSVGElement;
        svgClone.setAttribute('x', fo.getAttribute('x') || '0');
        svgClone.setAttribute('y', fo.getAttribute('y') || '0');
        svgClone.setAttribute('width', fo.getAttribute('width') || '100');
        svgClone.setAttribute('height', fo.getAttribute('height') || '100');
        console.log('Converted SVG foreignObject to native SVG');
        return svgClone;
      } catch (error) {
        console.warn('Failed to convert SVG foreignObject:', error);
        return null;
      }
    } else if (iconDiv) {
      // Create a placeholder for icons in PNG export
      const iconGroup = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'g'
      );

      const iconRect = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'rect'
      );
      iconRect.setAttribute('x', fo.getAttribute('x') || '0');
      iconRect.setAttribute('y', fo.getAttribute('y') || '0');
      iconRect.setAttribute('width', fo.getAttribute('width') || '24');
      iconRect.setAttribute('height', fo.getAttribute('height') || '24');
      iconRect.setAttribute('fill', '#666');
      iconRect.setAttribute('stroke', '#999');
      iconRect.setAttribute('stroke-width', '1');
      iconRect.setAttribute('rx', '2');

      const iconText = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      );
      iconText.setAttribute(
        'x',
        String(
          Number(fo.getAttribute('x') || '0') +
            Number(fo.getAttribute('width') || '24') / 2
        )
      );
      iconText.setAttribute(
        'y',
        String(
          Number(fo.getAttribute('y') || '0') +
            Number(fo.getAttribute('height') || '24') / 2 +
            4
        )
      );
      iconText.setAttribute('text-anchor', 'middle');
      iconText.setAttribute('font-family', 'Arial');
      iconText.setAttribute('font-size', '8');
      iconText.setAttribute('fill', '#fff');
      iconText.textContent = '⚡';

      iconGroup.appendChild(iconRect);
      iconGroup.appendChild(iconText);
      console.log('Created placeholder for icon in PNG export');
      return iconGroup;
    } else if (img && img.src) {
      // For PNG export, try to include the actual image
      try {
        const svgImage = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'image'
        );
        svgImage.setAttribute('x', fo.getAttribute('x') || '0');
        svgImage.setAttribute('y', fo.getAttribute('y') || '0');
        svgImage.setAttribute('width', fo.getAttribute('width') || '100');
        svgImage.setAttribute('height', fo.getAttribute('height') || '100');
        svgImage.setAttributeNS(
          'http://www.w3.org/1999/xlink',
          'href',
          img.src
        );

        // Apply any transform from the foreign object
        const transform = fo.getAttribute('transform');
        if (transform) {
          svgImage.setAttribute('transform', transform);
        }

        console.log(
          'Successfully converted image to SVG element for PNG export'
        );
        return svgImage;
      } catch (error) {
        console.warn('Failed to convert image for PNG export:', error);
        // Fallback to placeholder if image conversion fails
        const imageGroup = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'g'
        );
        const imageRect = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect'
        );
        imageRect.setAttribute('x', fo.getAttribute('x') || '0');
        imageRect.setAttribute('y', fo.getAttribute('y') || '0');
        imageRect.setAttribute('width', fo.getAttribute('width') || '100');
        imageRect.setAttribute('height', fo.getAttribute('height') || '100');
        imageRect.setAttribute('fill', '#333');
        imageRect.setAttribute('stroke', '#666');
        imageRect.setAttribute('stroke-width', '1');

        const imageText = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'text'
        );
        imageText.setAttribute(
          'x',
          String(
            Number(fo.getAttribute('x') || '0') +
              Number(fo.getAttribute('width') || '100') / 2
          )
        );
        imageText.setAttribute(
          'y',
          String(
            Number(fo.getAttribute('y') || '0') +
              Number(fo.getAttribute('height') || '100') / 2 +
              6
          )
        );
        imageText.setAttribute('text-anchor', 'middle');
        imageText.setAttribute('font-family', 'Arial');
        imageText.setAttribute('font-size', '12');
        imageText.setAttribute('fill', '#999');
        imageText.textContent = '🖼️';

        imageGroup.appendChild(imageRect);
        imageGroup.appendChild(imageText);
        console.log('Created placeholder for image in PNG export');
        return imageGroup;
      }
    } else if (textContent && textContent.length > 0) {
      // Convert text content to SVG text element for PNG export
      try {
        console.log('Converting text foreignObject to SVG text:', textContent);

        // Find the div element inside the foreign object
        const textDiv = fo.querySelector('div');
        if (!textDiv) {
          console.warn('No div found in foreign object');
          return null;
        }

        // Parse the inline styles from the div's style attribute
        const inlineStyle = textDiv.getAttribute('style') || '';
        console.log('Div inline style:', inlineStyle);

        // Extract style values using regex
        const getStyleValue = (property: string): string => {
          const regex = new RegExp(`${property}:\\s*([^;]+)`);
          const match = inlineStyle.match(regex);
          return match ? match[1]!.trim() : '';
        };

        const fontSize = getStyleValue('font-size');
        const fontFamily = getStyleValue('font-family').replace(/&quot;/g, '"');
        const color = getStyleValue('color');
        const textAlign = getStyleValue('text-align');
        const fontWeight = getStyleValue('font-weight');
        const letterSpacing = getStyleValue('letter-spacing');
        const alignItems = getStyleValue('align-items');
        const justifyContent = getStyleValue('justify-content');
        const whiteSpace = getStyleValue('white-space');

        console.log('Extracted styles:', {
          fontSize,
          fontFamily,
          color,
          textAlign,
          fontWeight,
          letterSpacing,
          alignItems,
          justifyContent,
          whiteSpace,
        });

        const svgText = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'text'
        );

        // Position - account for how text is positioned in foreign objects
        const foX = Number(fo.getAttribute('x') || '0');
        const foY = Number(fo.getAttribute('y') || '0');
        const foWidth = Number(fo.getAttribute('width') || '0');
        const foHeight = Number(fo.getAttribute('height') || '0');
        const fontSizeValue = parseFloat(fontSize) || 16;

        // For shape text, the foreign object has padding and the text is centered
        let textX = foX;
        let textY = foY;

        // Adjust for text alignment within the foreign object
        const horizontalAlign = textAlign || 'center';
        const verticalAlign = alignItems || 'center';

        // Horizontal positioning
        if (horizontalAlign === 'center') {
          textX = foX + foWidth / 2;
        } else if (horizontalAlign === 'right' || horizontalAlign === 'end') {
          textX = foX + foWidth;
        } else {
          textX = foX; // left alignment
        }

        // Vertical positioning - account for flex centering
        if (verticalAlign === 'center') {
          textY = foY + foHeight / 2 + fontSizeValue * 0.3;
        } else if (verticalAlign === 'flex-end') {
          textY = foY + foHeight - fontSizeValue * 0.2;
        } else {
          textY = foY + fontSizeValue;
        }

        svgText.setAttribute('x', String(textX));
        svgText.setAttribute('y', String(textY));

        // Font properties - use the exact extracted font family
        console.log('Using extracted font family:', fontFamily);

        // Use the exact font family from the extracted styles
        // For better compatibility, ensure web-safe fonts are prioritized
        const finalFontFamily =
          fontFamily ||
          'Caveat, Kalam, "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive';

        // For PNG export, use a font that's more likely to be available in canvas conversion
        // Since web fonts don't reliably work in SVG-to-canvas conversion, prioritize system fonts
        let pngFontFamily = finalFontFamily;

        if (finalFontFamily.includes('Caveat')) {
          // Try to use a system font that looks similar to Caveat
          // Use Brush Script MT or other handwritten system fonts as primary options
          pngFontFamily =
            '"Brush Script MT", "Lucida Handwriting", "Apple Chancery", "Segoe Print", "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive';
          console.log(
            'PNG Export: Using system handwritten fonts instead of web fonts'
          );
        }

        console.log('PNG: Setting font-family attribute to:', pngFontFamily);
        svgText.setAttribute('font-family', pngFontFamily);
        svgText.setAttribute('font-size', fontSize || '16px');
        svgText.setAttribute('font-weight', fontWeight || 'normal');
        svgText.setAttribute('font-style', 'normal');
        // Use letter spacing from extracted styles or default based on font family
        const extractedLetterSpacing =
          letterSpacing ||
          ((fontFamily || '').includes('JetBrains Mono') ||
          (fontFamily || '').includes('Fira Code') ||
          (fontFamily || '').includes('Consolas') ||
          (fontFamily || '').includes('Monaco') ||
          (fontFamily || '').includes('Courier')
            ? '0'
            : '0.5px');
        svgText.setAttribute('letter-spacing', extractedLetterSpacing);

        // Color
        if (color && color !== 'rgba(0, 0, 0, 0)') {
          svgText.setAttribute('fill', color);
        } else {
          svgText.setAttribute('fill', '#ffffff');
        }

        // Set text anchor based on horizontal alignment
        if (horizontalAlign === 'center') {
          svgText.setAttribute('text-anchor', 'middle');
        } else if (horizontalAlign === 'right' || horizontalAlign === 'end') {
          svgText.setAttribute('text-anchor', 'end');
        } else {
          svgText.setAttribute('text-anchor', 'start');
        }

        // Handle multi-line text and preserve whitespace
        if (
          whiteSpace === 'pre' ||
          whiteSpace === 'pre-wrap' ||
          whiteSpace === 'pre-line'
        ) {
          const lines = textContent.split('\n');

          // For multi-line text, adjust the starting position so the text block is centered
          if (lines.length > 1 && verticalAlign === 'center') {
            const totalTextHeight = lines.length * fontSizeValue * 1.2;
            const adjustedY =
              foY + foHeight / 2 - totalTextHeight / 2 + fontSizeValue * 0.8;
            svgText.setAttribute('y', String(adjustedY));
          }

          lines.forEach((line, index) => {
            const tspan = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'tspan'
            );
            tspan.setAttribute('x', svgText.getAttribute('x') || '0');
            tspan.setAttribute('dy', index === 0 ? '0' : '1.2em');
            tspan.textContent = line;
            svgText.appendChild(tspan);
          });
        } else {
          svgText.textContent = textContent;
        }

        // Apply any transform from the foreign object
        const transform = fo.getAttribute('transform');
        if (transform) {
          svgText.setAttribute('transform', transform);
        }

        console.log('Successfully converted text foreignObject to SVG text');
        return svgText;
      } catch (error) {
        console.warn('Failed to convert text foreignObject:', error);
        return null;
      }
    }

    // If we can't handle this foreign object, skip it
    return null;
  }

  // For non-foreignObject elements, clone and recursively clean children
  const cloned = element.cloneNode(false) as SVGElement;

  // Process children recursively
  Array.from(element.children).forEach(child => {
    const cleanedChild = cleanChildRecursively(child as SVGElement);
    if (cleanedChild) {
      cloned.appendChild(cleanedChild);
    }
  });

  return cloned;
}
